import { useState, useEffect, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import api, { BACKEND_URL } from "@/lib/api";
import axios from "axios";
import { toast } from "sonner";
import { getCoverUrl, getProfilePhotoUrl } from "@/lib/mediaUrls";
import { useShareVisibility } from "@/lib/useShareVisibility";

const API = `${BACKEND_URL}/api`;

const BadgeDisplay = ({ badges }) => {
  const badgeStyles = {
    founder: { bg: "bg-gradient-to-r from-yellow-500 to-yellow-600", text: "Founder", icon: "👑" },
    admin: { bg: "bg-gradient-to-r from-purple-500 to-purple-600", text: "Admin", icon: "⚡" },
    spotlight_winner: { bg: "bg-gradient-to-r from-blue-500 to-blue-600", text: "Spotlight Winner", icon: "⭐" },
    magazine_feature: { bg: "bg-gradient-to-r from-pink-500 to-pink-600", text: "Magazine Feature", icon: "📰" }
  };

  if (!badges || badges.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-2">
      {badges.map((badge) => {
        const style = badgeStyles[badge];
        if (!style) return null;
        return (
          <span
            key={badge}
            className={`${style.bg} text-white text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1`}
            data-testid={`badge-${badge}`}
          >
            <span>{style.icon}</span>
            {style.text}
          </span>
        );
      })}
    </div>
  );
};

const SubmissionSharePage = () => {
  const { submission_id } = useParams();
  const navigate = useNavigate();
  const [submission, setSubmission] = useState(null);
  const [artist, setArtist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [hasLiked, setHasLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  // Feb 2026 — CEO share-visibility gate for the public track share view.
  const { canShare } = useShareVisibility();
  const tracksShareOn = canShare("tracks");

  const checkAuth = useCallback(async () => {
    try {
      const response = await api.get(`/auth/me`);
      setCurrentUser(response.data);
    } catch (error) {
      // User not logged in - that's okay for public page
    }
  }, []);

  const fetchSubmission = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/submission/${submission_id}/public`);
      setSubmission(response.data.submission);
      setArtist(response.data.artist);
      
      const likes = response.data.submission.likes || [];
      setLikeCount(likes.length);
      
      // Check if current user has liked
      if (currentUser) {
        setHasLiked(likes.includes(currentUser.user_id));
      }
    } catch (error) {
      console.error("Failed to fetch submission:", error);
      toast.error("Submission not found");
    } finally {
      setLoading(false);
    }
  }, [submission_id, currentUser]);

  useEffect(() => {
    fetchSubmission();
    checkAuth();
  }, [submission_id, fetchSubmission, checkAuth]);

  const handleLike = async () => {
    if (!currentUser) {
      toast.error("Please login to like submissions");
      navigate("/login");
      return;
    }

    try {
      const response = await axios.post(
        `${API}/submission/${submission_id}/like`,
        null,
        { params: { user_id: currentUser.user_id } }
      );
      
      setHasLiked(response.data.liked);
      setLikeCount(response.data.like_count);
      
      toast.success(response.data.liked ? "Liked!" : "Unliked");
    } catch (error) {
      toast.error("Failed to like submission");
    }
  };

  const handleShare = () => {
    const url = window.location.href;
    navigator.clipboard.writeText(url);
    toast.success("Share link copied to clipboard!");
  };

  const togglePlay = () => {
    const audio = document.getElementById("audio-player");
    if (isPlaying) {
      audio.pause();
    } else {
      audio.play();
    }
    setIsPlaying(!isPlaying);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#1a1a1e' }}>
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
          <p className="mt-4 text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  if (!submission) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#1a1a1e' }}>
        <div className="text-center">
          <h2 className="text-2xl font-bold text-white mb-4">Submission Not Found</h2>
          <Link to="/"><Button className="submit-music-btn">Back Home</Button></Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: '#1a1a1e' }}>
      {/* Header */}
      <div className="border-b border-gray-800" style={{ background: '#0f0f11' }}>
        <div className="container-custom py-4">
          <Link to="/"><Button variant="ghost" className="text-purple-400">← Back</Button></Link>
        </div>
      </div>

      {/* Main Content */}
      <div className="container-custom py-12">
        <div className="max-w-4xl mx-auto">
          <Card className="p-8" style={{ background: 'rgba(42, 26, 58, 0.5)', borderColor: 'rgba(138, 43, 226, 0.3)' }}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Cover Art */}
              <div className="relative">
                <img
                  src={getCoverUrl(submission, 'cover')}
                  alt={submission.artist_name}
                  className="w-full rounded-lg shadow-2xl"
                  data-testid="submission-cover"
                />
                
                {/* Play Button Overlay */}
                <button
                  onClick={togglePlay}
                  className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-lg hover:bg-black/50 transition-all"
                  data-testid="play-button"
                >
                  <div className="w-20 h-20 rounded-full bg-purple-600 flex items-center justify-center hover:scale-110 transition-transform">
                    {isPlaying ? (
                      <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/>
                      </svg>
                    ) : (
                      <svg className="w-8 h-8 text-white ml-1" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M8 5v14l11-7z"/>
                      </svg>
                    )}
                  </div>
                </button>
              </div>

              {/* Info */}
              <div>
                <h1 className="text-3xl font-bold text-white mb-2" data-testid="submission-title">
                  {submission.artist_name}
                </h1>
                
                <p className="text-purple-400 text-lg mb-4" data-testid="submission-genre">
                  {submission.genre}
                </p>

                {artist && (
                  <Link to={`/artist/${artist.user_id}`} className="inline-block mb-4">
                    <div className="flex items-center gap-3 p-3 bg-purple-900/30 rounded-lg hover:bg-purple-900/40 transition-all">
                      {(artist.profile_photo || artist.picture) && (
                        <img src={getProfilePhotoUrl(artist)} alt={artist.name} className="w-10 h-10 rounded-full" />
                      )}
                      <div>
                        <p className="text-white font-semibold">{artist.name}</p>
                        <BadgeDisplay badges={artist.badges} />
                      </div>
                    </div>
                  </Link>
                )}

                <div className="mb-6">
                  <p className="text-gray-300 leading-relaxed" data-testid="submission-bio">
                    {submission.bio}
                  </p>
                </div>

                {/* Stats */}
                <div className="flex gap-6 mb-6">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-purple-400" data-testid="play-count">
                      {(submission.display_plays
                        ?? ((submission.play_count || 0) + (submission.promotional_plays || 0))
                      ).toLocaleString()}
                    </div>
                    <div className="text-gray-400 text-sm">Plays</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-pink-400" data-testid="like-count">
                      {likeCount}
                    </div>
                    <div className="text-gray-400 text-sm">Likes</div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-3">
                  <Button
                    onClick={handleLike}
                    className={`flex-1 ${hasLiked ? 'bg-pink-600 hover:bg-pink-700' : 'bg-gray-700 hover:bg-gray-600'}`}
                    data-testid="like-button"
                  >
                    ❤️ {hasLiked ? 'Liked' : 'Like'}
                  </Button>
                  {tracksShareOn && (
                    <Button
                      onClick={handleShare}
                      variant="outline"
                      className="flex-1 text-white border-purple-500"
                      data-testid="share-button"
                    >
                      🔗 Share
                    </Button>
                  )}
                </div>

                {/* Social Links */}
                {(submission.instagram || submission.youtube || submission.tiktok || submission.spotify) && (
                  <div className="mt-6 pt-6 border-t border-gray-700">
                    <p className="text-gray-400 text-sm mb-3">Follow the artist:</p>
                    <div className="flex flex-wrap gap-2">
                      {submission.instagram && (
                        <a href={submission.instagram} target="_blank" rel="noopener noreferrer" className="social-link-btn text-xs">
                          Instagram
                        </a>
                      )}
                      {submission.tiktok && (
                        <a href={submission.tiktok} target="_blank" rel="noopener noreferrer" className="social-link-btn text-xs">
                          TikTok
                        </a>
                      )}
                      {submission.youtube && (
                        <a href={submission.youtube} target="_blank" rel="noopener noreferrer" className="social-link-btn text-xs">
                          YouTube
                        </a>
                      )}
                      {submission.spotify && (
                        <a href={submission.spotify} target="_blank" rel="noopener noreferrer" className="social-link-btn text-xs">
                          Spotify
                        </a>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </Card>

          {/* Hidden Audio Player */}
          <audio
            id="audio-player"
            src={`${API}/public/music/${submission.music_file}`}
            onEnded={() => setIsPlaying(false)}
            style={{ display: 'none' }}
          />
        </div>
      </div>
    </div>
  );
};

export default SubmissionSharePage;
